import { theme } from '@breakroom-nx/assets';
import {
  ThumbUpIcon as TUOutline,
  ThumbDownIcon as TDOutline,
} from 'react-native-heroicons/outline';
import {
  ThumbUpIcon as TUSolid,
  ThumbDownIcon as TDSolid,
} from 'react-native-heroicons/solid';

import { IconButton, LocalComponentError } from '../..';
import { VStack, HStack, Text, Box, Center, Spinner } from 'native-base';
import React, { useEffect, useState } from 'react';
import {
  useAppDispatch,
  openModal,
  useGetRatingQuery,
} from '@breakroom-nx/redux';

import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { httpsCallable } from 'firebase/functions';
import { EntityBeingReported } from '@breakroom-nx/helpers';
import { functions } from '@breakroom-nx/apis';

export interface FeedbackRatingProps {
  readonly uid: string;
  readonly name: string;
  readonly entity: EntityBeingReported;
}

export enum FeedbackType {
  Scold,
  Praise,
  None,
}

export function FeedbackRating({ uid, name, entity }: FeedbackRatingProps) {
  const {
    data: rating,
    isLoading,
    refetch,
  } = useGetRatingQuery({ uid: uid, entityBeingReported: entity });
  const [feedback, setFeedback] = useState(FeedbackType.None);

  useEffect(() => {
    // console.log(`Feedback: ${JSON.stringify(rating)}`);
    if (rating?.upvoted) {
      if (rating.upvoted) {
        setFeedback(FeedbackType.Praise);
      } else {
        setFeedback(FeedbackType.Scold);
      }
    }
  }, [rating]);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorString, setErrorString] = useState<string>();

  const sendFeedback = httpsCallable(functions, 'user-sendfeedback');
  const dispatch = useAppDispatch();

  const scoldButton = () => {
    switch (feedback) {
      case FeedbackType.Scold:
        return <TDSolid color={theme.colors.red[200]} size={20} />;
      case FeedbackType.Praise:
        return <TDSolid color={theme.colors.surface[400]} size={20} />;
      case FeedbackType.None:
        return <TDOutline color={theme.colors.red[200]} size={20} />;
    }
  };

  const praiseButton = () => {
    switch (feedback) {
      case FeedbackType.Scold:
        return <TUSolid color={theme.colors.surface[400]} size={20} />;
      case FeedbackType.Praise:
        return <TUSolid color={theme.colors.primary[400]} size={20} />;
      case FeedbackType.None:
        return <TUOutline color={theme.colors.primary[400]} size={20} />;
    }
  };

  const getRating = () => {
    if (rating?.ratio || rating?.ratio == 0) {
      switch (true) {
        case rating.ratio >= 75:
          return '❤️';
        case rating.ratio >= 50:
          return '💔';
        case rating.ratio >= 25:
          return '😐';
        default:
          return '🚩';
      }
    } else {
      return '❔';
    }
  };

  async function submitReview(upvote: boolean, report?: string) {
    setIsSubmitting(true);
    await sendFeedback({
      uid: uid,
      entity: entity,
      upvote: upvote,
      report: report,
    })
      .then(() => {
        refetch();
        setIsSubmitting(false);
      })
      .catch((err) => {
        setErrorString('Error submitting feedback, please try again.');
        setIsSubmitting(false);
      });
  }

  return (
    <VStack space={5} alignItems={'center'}>
      <LocalComponentError error={errorString} />
      <Text color={theme.colors.surface[500]} fontSize={13} fontWeight={600}>
        Is
        <Text color={theme.colors.surface[600]} fontWeight={'bold'}>
          {` ${name} `}
        </Text>
        being a good sport?
      </Text>
      <VStack>
        <HStack alignItems={'center'} space={3}>
          <IconButton
            disabled={isLoading || isSubmitting}
            icon={scoldButton()}
            tooltipText={`Scold`}
            onPress={() => {
              dispatch(
                openModal({
                  modalType: 'Report',
                  modalProps: {
                    uid: uid,
                    name: name,
                    entity: entity,
                  },
                })
              );
            }}
          />
          <Text
            color={
              rating?.downvotes && rating?.downvotes > 0
                ? theme.colors.red[400]
                : theme.colors.surface[400]
            }
            fontWeight={'bold'}
            fontSize={13}
          >
            {rating?.downvotes}
          </Text>
          {isLoading || isSubmitting ? (
            <Spinner size={'sm'} />
          ) : (
            <Box size={'50px'} alignItems={'center'} alignContent={'center'}>
              <Center height={'full'} width={'full'}>
                <Box
                  position={'absolute'}
                  rounded={'full'}
                  alignSelf={'center'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  size={'70%'}
                >
                  <Text>{getRating()}</Text>
                </Box>
                <CircularProgressbar
                  value={rating?.ratio ?? 0}
                  strokeWidth={8}
                  styles={buildStyles({
                    rotation: 0,
                    strokeLinecap: 'round',
                    pathTransitionDuration: 0.5,
                    pathColor: theme.colors.primary[400],
                    trailColor:
                      rating?.upvotes || rating?.downvotes
                        ? theme.colors.red[400]
                        : theme.colors.surface[400],
                  })}
                />
              </Center>
            </Box>
          )}
          <Text
            color={
              rating?.upvotes && rating?.upvotes > 0
                ? theme.colors.primary[400]
                : theme.colors.surface[400]
            }
            fontWeight={'bold'}
            fontSize={13}
          >
            {rating?.upvotes}
          </Text>
          <IconButton
            disabled={isLoading || isSubmitting}
            icon={praiseButton()}
            tooltipText={`Praise`}
            onPress={() => {
              setFeedback(
                feedback !== FeedbackType.Praise
                  ? FeedbackType.Praise
                  : FeedbackType.None
              );
              submitReview(true);
            }}
          />
        </HStack>
      </VStack>
    </VStack>
  );
}

export default React.memo(FeedbackRating);
